import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, useReducedMotion, AnimatePresence } from "framer-motion";
import { ArrowRight, Award, Sparkles, Hammer, Leaf, X, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { KineticHeading } from "@/components/motion/KineticHeading";
import { Magnetic } from "@/components/motion/Magnetic";

const VALUES = [
  { icon: Award, title: "Inspired by Champions", text: "Each piece is rooted in the legacy of world-class horses — crafted to honour their story and spirit." },
  { icon: Sparkles, title: "Equestrian Elegance", text: "Designs that blend stable-born authenticity with refined luxury for those who appreciate fine taste." },
  { icon: Hammer, title: "Artisan Craftsmanship", text: "Every product is carefully handmade using high-quality materials, ensuring lasting beauty." },
  { icon: Leaf, title: "Sustainably Minded", text: "Organic soy wax, ethical materials and conscious packaging for a cleaner luxury experience." },
];

const CATEGORIES = [
  { slug: "jewellery", name: "Jewellery", img: "/images/cat-jewellery.jpg", tag: "Bespoke gold bangles — wearable tributes to legendary horses, made to your measure." },
  { slug: "lifestyle", name: "Lifestyle", img: "/images/cat-lifestyle.jpg", tag: "Candles, cushions and signature pieces that bring the lifestyle home." },
];

const TESTIMONIALS = [
  { name: "Charlotte Greene", place: "Dublin, Ireland", text: "I got the Ooh La La Bangle as a gift and honestly… I wear it everywhere. It feels like carrying a piece of the sport with you." },
  { name: "Isabelle Hart", place: "Gloucestershire, UK", text: "As a lifelong horse lover, the candle instantly transported me to the stables. Warm, leathery, utterly luxurious. I've bought three already!" },
  { name: "Michael Byrne", place: "Limerick, Ireland", text: "Equine Evolve understands horse culture like no other. I bought the Gilton Bangle for my daughter and she hasn't taken it off since." },
];

const GALLERY = [
  { img: "/images/gilton.jpg", label: "The Gilton Bangle", sub: "9ct gold" },
  { img: "/images/oohlala.jpg", label: "The Ooh La La Bangle", sub: "14ct gold · diamonds" },
  { img: "/images/cat-lifestyle.jpg", label: "Signature Candle", sub: "Organic soy wax" },
  { img: "/images/going-global.png", label: "Going Global", sub: "Olympic prestige" },
  { img: "/images/cushion.jpeg", label: "Huntmaster Cushion", sub: "Countryside charm" },
  { img: "/images/brand-hero.jpg", label: "The Boutique", sub: "Made to order" },
];

/* ---------- Shoppable Hero — interactive boutique room with hotspots ---------- */
function HotspotModal({ spot, onClose }) {
  const single = spot.items.length === 1;
  const p0 = spot.items[0];
  return (
    <motion.div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="hotspot-modal"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="hotspot-modal-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-3xl bg-[#1A1614] border border-[#D4AF37]/25 shadow-[0_30px_90px_rgba(0,0,0,0.7)] max-h-[88vh] overflow-y-auto no-scrollbar"
      >
        <button onClick={onClose} data-testid="hotspot-modal-close" className="absolute top-4 right-4 z-10 h-10 w-10 flex items-center justify-center border border-white/15 bg-[#0C0A09]/70 text-[#F5F5F4] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
          <X size={18} />
        </button>

        {single ? (
          <div className="grid sm:grid-cols-2">
            <div className="aspect-square sm:aspect-auto overflow-hidden bg-[#0C0A09]">
              <img src={p0.images?.[0]} alt={p0.name} className="h-full w-full object-cover" />
            </div>
            <div className="p-7 sm:p-9 flex flex-col">
              <p className="ee-overline mb-2">{spot.label}</p>
              <h3 className="font-display text-3xl tracking-tight leading-tight">{p0.name}</h3>
              {p0.short_description && <p className="text-[#A39B95] text-sm leading-relaxed mt-4">{p0.short_description}</p>}
              <p className="font-display text-3xl ee-gold-text mt-auto pt-6">{eur(p0.price)}</p>
              <Link to={`/product/${p0.slug}`} data-testid={`hotspot-view-${p0.slug}`} className="mt-5 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0C0A09] px-7 py-3.5 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors">
                View Product <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="p-7 sm:p-9">
            <p className="ee-overline mb-2">{spot.label}</p>
            <h3 className="font-display text-3xl tracking-tight mb-7">Bespoke gold bangles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {spot.items.map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} data-testid={`hotspot-view-${p.slug}`} className="group block border border-white/5 bg-[#0C0A09] hover:border-[#D4AF37]/40 transition-colors">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-display text-xl tracking-tight leading-tight">{p.name}</h4>
                    <p className="ee-gold-text font-display text-lg mt-1">{eur(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function Hero({ products }) {
  const [activeId, setActiveId] = useState(null);
  const candle = products.find((p) => p.slug === "equine-evolve-signature-candle");
  const cushion = products.find((p) => p.slug === "equine-evolve-tally-ho-cushion");
  const bangles = products.filter((p) => p.category === "jewellery").slice(0, 3);

  const spots = [
    cushion && { id: "cushion", x: 15, y: 73, label: "Tally Ho Cushion", items: [cushion] },
    candle && { id: "candle", x: 46, y: 41, label: "Signature Candle", items: [candle] },
    bangles.length >= 3 && { id: "jewellery", x: 85, y: 50, label: "The Bangles", items: bangles },
  ].filter(Boolean);
  const active = spots.find((s) => s.id === activeId);

  return (
    <section
      className="relative w-full overflow-hidden bg-[#0C0A09] ee-grain"
      data-testid="hero"
      style={{ backgroundImage: "url(/images/ee-slider-bg.webp)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-[#0C0A09]/55" />
      <div className="relative mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-14 pt-28 pb-16 lg:min-h-[100svh] flex flex-col justify-center">
        {/* heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-4xl mx-auto mb-9 lg:mb-12"
        >
          <p className="ee-overline mb-5">Welcome to the Equine Evolve Boutique</p>
          <KineticHeading as="h1" trigger="mount" text="Luxury Equestrian Living." className="font-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight" stagger={0.08} delay={0.2} />
          <KineticHeading as="h2" trigger="mount" text="Redefined." className="font-display font-light italic text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight ee-gold-text" stagger={0.08} delay={0.55} />
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }} className="text-[#A39B95] mt-6 max-w-xl mx-auto text-sm sm:text-base">
            Step inside the boutique — tap the <span className="ee-gold-text">+</span> markers to discover each piece.
          </motion.p>
        </motion.div>

        {/* interactive room scene */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-[1180px] mx-auto rounded-sm overflow-hidden border border-[#D4AF37]/20 shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
        >
          <img src="/images/ee-room.jpg" alt="The Equine Evolve boutique interior" className="w-full h-auto block select-none" draggable="false" />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/25 via-transparent to-black/10" />
          {spots.map((s) => (
            <button
              key={s.id} data-testid={`hotspot-${s.id}`} aria-label={s.label} onClick={() => setActiveId(s.id)}
              style={{ left: `${s.x}%`, top: `${s.y}%` }}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
            >
              <span className="ee-hotspot-ring absolute left-1/2 top-1/2 h-9 w-9 sm:h-11 sm:w-11 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#D4AF37]/60" />
              <span className="relative flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-[#0C0A09]/80 border border-[#D4AF37] text-[#D4AF37] backdrop-blur-sm transition-transform group-hover:scale-110">
                <Plus size={18} />
              </span>
              <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap bg-[#0C0A09]/90 border border-[#D4AF37]/30 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-[#F5F5F4] opacity-0 group-hover:opacity-100 transition-opacity">
                {s.label}
              </span>
            </button>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mt-9 lg:mt-12"
        >
          <Magnetic strength={0.4}>
            <Link to="/shop" data-testid="hero-shop-btn" className="group bg-[#D4AF37] text-[#0C0A09] px-9 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors flex items-center justify-center gap-2">
              Shop the Collection <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Magnetic>
          <Magnetic strength={0.4}>
            <Link to="/category/jewellery" data-testid="hero-jewellery-btn" className="border border-[#D4AF37] text-[#D4AF37] px-9 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors text-center">
              Fine Jewellery
            </Link>
          </Magnetic>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && <HotspotModal spot={active} onClose={() => setActiveId(null)} />}
      </AnimatePresence>
    </section>
  );
}

/* ---------- How it works (buying process) ---------- */
const STEPS = [
  { n: "01", t: "Choose your piece", d: "Explore the collection and select the bangle that speaks to you." },
  { n: "02", t: "Order securely", d: "Reserve your bespoke piece online — note any personalisation ideas at checkout." },
  { n: "03", t: "We take your measurements", d: "Our craftspeople personally contact you for your exact wrist size and any unique details to incorporate." },
  { n: "04", t: "Handcrafted & delivered", d: "Your bangle is made by hand in Ireland and shipped to you, with tracking." },
];

/* Mobile — golden-thread timeline that lights up on scroll */
function GoldenThread() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 78%", "end 55%"] });
  const fill = useSpring(useTransform(scrollYProgress, [0, 1], ["0%", "100%"]), { stiffness: 90, damping: 24, mass: 0.4 });

  return (
    <div ref={ref} className="lg:hidden relative pl-14 mt-14" data-testid="how-it-works-timeline">
      {/* track */}
      <div className="absolute left-5 top-1 bottom-1 w-px bg-white/10" />
      {!reduce && <motion.div style={{ height: fill }} className="absolute left-5 top-1 w-px bg-[#D4AF37] origin-top shadow-[0_0_12px_rgba(212,175,55,0.6)]" />}
      <div className="space-y-12">
        {STEPS.map((s) => (
          <Reveal key={s.n}>
            <div className="relative">
              <span className="absolute -left-14 -top-1 flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#0C0A09] font-display ee-gold-text text-sm">{s.n}</span>
              <h3 className="font-display text-2xl tracking-wide mb-2">{s.t}</h3>
              <p className="text-[#A8A29E] text-sm leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <section className="py-24 md:py-36 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto" data-testid="how-it-works">
      <Reveal>
        <p className="ee-overline mb-3">Before you buy</p>
        <KineticHeading text="How it works" className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5" />
        <p className="text-[#A8A29E] max-w-2xl leading-relaxed">
          Every bangle is bespoke and made to order. Here's the simple, white-glove process — from order to delivery.
        </p>
      </Reveal>
      {/* Desktop — top-border grid */}
      <div className="hidden lg:grid lg:grid-cols-4 gap-8 mt-14">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1}>
            <div className="border-t-2 border-[#D4AF37]/30 pt-6 h-full">
              <p className="font-display text-4xl ee-gold-text mb-4">{s.n}</p>
              <h3 className="font-display text-2xl tracking-wide mb-3">{s.t}</h3>
              <p className="text-[#A8A29E] text-sm leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <GoldenThread />
      <Reveal>
        <Link to="/faq" data-testid="how-faq-link" className="inline-flex items-center gap-2 mt-12 text-[#D4AF37] text-sm uppercase tracking-[0.15em] hover:gap-3 transition-all">
          Read the full FAQ <ArrowRight size={15} />
        </Link>
      </Reveal>
    </section>
  );
}

/* ---------- Mobile "Cinematic Vault" — sticky stacking card deck ---------- */
function VaultCard({ p, i, y, scale, opacity, imgY }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center px-5 pointer-events-none" style={{ zIndex: 10 + i * 10 }}>
    <motion.div
      style={{ y, scale, opacity, willChange: "transform, opacity" }}
      className="pointer-events-auto w-full max-w-md h-[58svh] max-h-[480px]"
      data-testid={`vault-card-${p.slug}`}
    >
      <div className="relative h-full w-full overflow-hidden border border-[#D4AF37]/25 bg-[#1A1614] shadow-[0_24px_70px_rgba(0,0,0,0.65)]">
        {/* image */}
        <div className="relative h-[56%] overflow-hidden">
          <motion.img
            src={p.images?.[0]}
            alt={p.name}
            style={{ y: imgY, willChange: "transform" }}
            className="absolute -top-[10%] left-0 h-[120%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1614] via-[#1A1614]/10 to-transparent" />
          <span className="absolute top-4 right-5 font-display text-4xl ee-gold-text leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">0{i + 1}</span>
        </div>
        {/* copy */}
        <div className="flex h-[44%] flex-col p-6">
          <p className="ee-overline mb-1.5">Signature Piece</p>
          <h3 className="font-display text-3xl tracking-tight leading-none">{p.name}</h3>
          {p.inspired_by && <p className="text-[#A39B95] italic text-sm mt-1.5">{p.inspired_by}</p>}
          <p className="font-display text-2xl ee-gold-text mt-auto">{eur(p.price)}<span className="text-[#57534E] text-[10px] ml-2 font-sans align-middle">excl. VAT</span></p>
          <Link
            to={`/product/${p.slug}`}
            data-testid={`vault-link-${p.slug}`}
            className="mt-3 inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 uppercase tracking-[0.15em] text-xs hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors"
          >
            Discover <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </motion.div>
    </div>
  );
}

function MobileVault({ bangles }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const sp = useSpring(scrollYProgress, { stiffness: 80, damping: 22, mass: 0.4 });

  // Card 1 — recedes as card 2 rises over it
  const c1y = useTransform(sp, [0, 0.34], [0, -28]);
  const c1s = useTransform(sp, [0, 0.34], [1, 0.92]);
  const c1o = useTransform(sp, [0, 0.42], [1, 0.3]);
  const c1img = useTransform(sp, [0, 0.34], ["7%", "-7%"]);
  // Card 2 — rises, then recedes as card 3 rises
  const c2y = useTransform(sp, [0.12, 0.34], ["104%", "0%"]);
  const c2s = useTransform(sp, [0.34, 0.68], [1, 0.92]);
  const c2o = useTransform(sp, [0.34, 0.76], [1, 0.3]);
  const c2img = useTransform(sp, [0.12, 0.5], ["7%", "-7%"]);
  // Card 3 — rises and rests
  const c3y = useTransform(sp, [0.46, 0.7], ["104%", "0%"]);
  const c3img = useTransform(sp, [0.46, 1], ["7%", "-7%"]);

  if (reduce) {
    return (
      <section className="lg:hidden bg-[#0C0A09] px-6 py-20" data-testid="showcase-mobile">
        <p className="ee-overline mb-3">Signature pieces</p>
        <h2 className="font-display text-4xl tracking-tight mb-10">The bangles</h2>
        <div className="space-y-12">
          {bangles.slice(0, 3).map((p, i) => (
            <Link key={p.id} to={`/product/${p.slug}`} data-testid={`vault-card-${p.slug}`} className="block border border-[#D4AF37]/25 bg-[#1A1614] overflow-hidden">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" />
              </div>
              <div className="p-6">
                <p className="ee-overline mb-1.5">Signature Piece · 0{i + 1}</p>
                <h3 className="font-display text-3xl tracking-tight leading-tight">{p.name}</h3>
                {p.inspired_by && <p className="text-[#A39B95] italic mt-2">{p.inspired_by}</p>}
                <p className="font-display text-2xl ee-gold-text mt-4">{eur(p.price)}</p>
                <Link to={`/product/${p.slug}`} data-testid={`vault-link-${p.slug}`} className="ee-gold-text text-sm mt-4 inline-flex items-center gap-2">Discover <ArrowRight size={14} /></Link>
              </div>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  const cards = [
    { p: bangles[0], y: c1y, scale: c1s, opacity: c1o, imgY: c1img },
    { p: bangles[1], y: c2y, scale: c2s, opacity: c2o, imgY: c2img },
    { p: bangles[2], y: c3y, scale: 1, opacity: 1, imgY: c3img },
  ];

  return (
    <section ref={ref} className="lg:hidden relative h-[320vh] bg-[#0C0A09]" data-testid="showcase-mobile">
      <div className="sticky top-0 h-[100svh] overflow-hidden flex items-center justify-center">
        <div className="absolute top-0 inset-x-0 px-6 pt-20 pointer-events-none">
          <p className="ee-overline mb-2">Signature pieces</p>
          <h2 className="font-display text-4xl tracking-tight">The bangles</h2>
        </div>
        {cards.map((c, i) => (
          <VaultCard key={c.p.id} p={c.p} i={i} y={c.y} scale={c.scale} opacity={c.opacity} imgY={c.imgY} />
        ))}
      </div>
    </section>
  );
}

/* ---------- Sticky crossfade showcase (Apple-style) ---------- */
function Showcase({ bangles }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  // three segments
  const op1 = useTransform(scrollYProgress, [0, 0.06, 0.28, 0.36], [0, 1, 1, 0]);
  const op2 = useTransform(scrollYProgress, [0.32, 0.40, 0.60, 0.68], [0, 1, 1, 0]);
  const op3 = useTransform(scrollYProgress, [0.64, 0.72, 0.96, 1], [0, 1, 1, 1]);
  const sc1 = 1;
  const sc2 = 1;
  const sc3 = 1;
  const rot = useTransform(scrollYProgress, [0, 1], [-4, 4]);
  const barH = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const ops = [op1, op2, op3];
  const scs = [sc1, sc2, sc3];
  // Gate pointer-events on opacity so hidden crossfaded copies aren't interactive
  const pe1 = useTransform(op1, (v) => (v < 0.5 ? "none" : "auto"));
  const pe2 = useTransform(op2, (v) => (v < 0.5 ? "none" : "auto"));
  const pe3 = useTransform(op3, (v) => (v < 0.5 ? "none" : "auto"));
  const pes = [pe1, pe2, pe3];
  if (bangles.length < 3) return null;

  return (
    <>
      {/* Mobile — cinematic stacking vault (desktop keeps the pinned crossfade below) */}
      <MobileVault bangles={bangles} />

      {/* Desktop — Apple-style pinned crossfade */}
      <section ref={ref} className="hidden lg:block relative h-[320vh] bg-[#0C0A09]">
      <div className="sticky top-0 h-screen overflow-hidden flex items-center">
        <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-2 gap-10 items-center">
          {/* Left — crossfading copy */}
          <div className="relative h-[60vh] lg:h-[70vh] flex items-center">
            {bangles.slice(0, 3).map((p, i) => (
              <motion.div key={p.id} style={{ opacity: ops[i], pointerEvents: pes[i] }} className="absolute inset-0 flex flex-col justify-center" data-testid={`showcase-copy-${i}`}>
                <p className="ee-overline mb-5">Signature Piece · 0{i + 1}</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1]">{p.name}</h2>
                {p.inspired_by && <p className="text-[#A39B95] italic mt-4 text-lg">{p.inspired_by}</p>}
                <p className="text-[#A39B95] mt-6 max-w-md leading-relaxed">{p.short_description}</p>
                <p className="font-display text-3xl ee-gold-text mt-7">{eur(p.price)}<span className="text-[#57534E] text-xs ml-2 font-sans">excl. VAT</span></p>
                <Magnetic strength={0.3}>
                  <Link to={`/product/${p.slug}`} data-testid={`showcase-link-${p.slug}`} className="mt-8 inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-8 py-3.5 uppercase tracking-[0.15em] text-sm hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors">
                    Discover <ArrowRight size={15} />
                  </Link>
                </Magnetic>
              </motion.div>
            ))}
          </div>

          {/* Right — pinned crossfading imagery */}
          <div className="relative h-[55vh] lg:h-[75vh]">
            {bangles.slice(0, 3).map((p, i) => (
              <motion.div key={p.id} style={{ opacity: ops[i], scale: scs[i], rotate: rot, willChange: "transform, opacity" }} className="absolute inset-0">
                <div className="relative h-full w-full overflow-hidden border ee-hairline bg-[#1A1614]">
                  <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
              </motion.div>
            ))}
            {/* progress bar */}
            <div className="absolute -left-6 top-0 h-full w-[2px] bg-[#292524] hidden lg:block">
              <motion.div style={{ height: barH }} className="w-full bg-[#D4AF37]" />
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

/* ---------- Kinetic interlude ---------- */
function Interlude() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[80vh] overflow-hidden flex items-center justify-center ee-grain">
      <motion.div style={{ y, willChange: "transform" }} className="absolute inset-0">
        <img src="/images/candle.jpg" alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0C0A09]/70" />
      </motion.div>
      <div className="relative text-center px-6">
        <p className="ee-overline mb-6">The Equine Evolve Ethos</p>
        <KineticHeading text="Crafted in Obsidian & Gold." className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight max-w-5xl" stagger={0.08} />
      </div>
    </section>
  );
}

/* ---------- Horizontal sticky gallery ---------- */
function HorizontalGallery() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-62%"]);
  return (
    <section ref={ref} className="relative h-[300vh] bg-[#0C0A09]">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24 mb-10">
          <p className="ee-overline mb-3">The Collection</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">A closer look</h2>
        </div>
        <motion.div style={{ x, willChange: "transform" }} className="flex gap-6 lg:gap-8 px-6 md:px-12 lg:px-24">
          {GALLERY.map((g, i) => (
            <div key={i} className="relative flex-shrink-0 w-[78vw] sm:w-[44vw] lg:w-[30vw] aspect-[3/4] overflow-hidden border ee-hairline bg-[#1A1614]">
              <img src={g.img} alt={g.label} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 p-7">
                <p className="ee-overline mb-1">{g.sub}</p>
                <h3 className="font-display text-2xl md:text-3xl">{g.label}</h3>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const bangles = products.filter((p) => p.category === "jewellery");

  return (
    <>
      <Seo jsonLd={{ "@context": "https://schema.org", "@type": "Store", name: "Equine Evolve", slogan: "Luxury Equestrian Living. Redefined." }} />

      <Hero products={products} />
      {bangles.length >= 3 && <Showcase bangles={bangles} />}
      <HowItWorks />
      <Interlude />
      <HorizontalGallery />

      {/* CATEGORIES */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
        <Reveal>
          <p className="ee-overline mb-3">Our Categories</p>
          <KineticHeading text="Curated worlds" className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight mb-14" />
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.12}>
              <Link to={`/category/${c.slug}`} data-testid={`home-category-${c.slug}`} className="group block relative aspect-[3/4] overflow-hidden border ee-hairline" data-magnetic>
                <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <h3 className="font-display text-3xl tracking-wide">{c.name}</h3>
                  <p className="text-[#A39B95] text-sm mt-2 max-w-xs">{c.tag}</p>
                  <span className="ee-gold-text text-sm mt-4 inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                    Explore <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative py-24 md:py-40 ee-grain">
        <div className="absolute inset-0">
          <img src="/images/about.jpg" alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0C0A09]/85" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
          <Reveal>
            <p className="ee-overline mb-3">Why choose us</p>
            <KineticHeading text="We honour legacies." className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl" />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mt-16">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <v.icon strokeWidth={1} className="ee-gold-text mb-5" size={32} />
                <h3 className="font-display text-2xl tracking-wide mb-3">{v.title}</h3>
                <p className="text-[#A39B95] text-sm leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
        <Reveal>
          <p className="ee-overline mb-3 text-center">Client feedback</p>
          <KineticHeading text="Hear from clients" className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-center mb-16 justify-center flex flex-wrap" />
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.12}>
              <div className="border ee-hairline bg-[#1A1614] p-8 h-full transition-colors duration-500 hover:border-[#D4AF37]/40">
                <p className="text-[#D4AF37] text-4xl font-display mb-4 leading-none">"</p>
                <p className="text-[#A39B95] leading-relaxed italic">{t.text}</p>
                <div className="mt-6 pt-6 border-t ee-hairline">
                  <p className="font-display text-xl">{t.name}</p>
                  <p className="text-xs text-[#57534E] mt-1">{t.place}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative py-32 md:py-48 text-center overflow-hidden ee-grain">
        <div className="absolute inset-0">
          <img src="/images/gilton-hero.jpg" alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0C0A09]/80" />
        </div>
        <div className="relative px-6">
          <KineticHeading text="Wear a legend." className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight justify-center flex flex-wrap" />
          <p className="text-[#A39B95] mt-7 max-w-xl mx-auto">Each piece is made to order, by hand — a tribute to a champion that becomes part of your own story.</p>
          <Magnetic strength={0.4}>
            <Link to="/shop" data-testid="cta-shop-btn" className="inline-flex mt-10 bg-[#D4AF37] text-[#0C0A09] px-12 py-5 uppercase tracking-[0.18em] text-sm hover:bg-[#B5952F] transition-colors items-center gap-2">
              Explore the Collection <ArrowRight size={16} />
            </Link>
          </Magnetic>
        </div>
      </section>
    </>
  );
}
